/**
 * Format an IANA timezone identifier as a friendly city name.
 * "America/Los_Angeles" → "Los Angeles". Falls back to the original string when
 * the identifier doesn't have the expected `region/city` shape.
 */
export function getCityFromTimezone(timezone: string): string {
  const lastSegment = timezone.split("/").pop();
  if (!lastSegment) {
    return timezone;
  }
  return lastSegment.replace(/_/g, " ");
}

/**
 * Resolve the current short timezone abbreviation for an IANA identifier.
 * Uses `Intl.DateTimeFormat` with the `en-US` locale because that's the only
 * locale that reliably returns US-style short names ("PDT", "EST", "CDT", …);
 * other locales — and date-fns-tz's `zzz` token — return a `"GMT-7"` offset
 * instead. DST-aware via the supplied `now`.
 *
 * Returns undefined when the runtime falls back to a `GMT…`/`UTC…` offset
 * (e.g. non-US zones with no well-known short name) so callers can skip the
 * abbreviation rather than rendering a less-useful offset string.
 */
export function getTimezoneAbbreviation(
  timezone: string,
  now: Date = new Date()
): string | undefined {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    }).formatToParts(now);
    const abbreviation = parts.find((part) => part.type === "timeZoneName")?.value;
    if (!abbreviation || /^(GMT|UTC)/i.test(abbreviation)) {
      return undefined;
    }
    return abbreviation;
  } catch {
    return undefined;
  }
}
